<template>
  <div class="inline-product-form">
    <div class="inline-form-grid">
      <div class="inline-form-group inline-form-group--wide">
        <base-label :required="true">Название</base-label>
        <base-input
          v-model="form.name"
          placeholder="Название продукта"
          :invalid="errors.name"
          @input="clearError('name')"
        />
        <span v-if="errors.name" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="inline-form-group inline-form-group--wide">
        <base-label :required="true">Описание</base-label>
        <base-input
          v-model="form.description"
          placeholder="Описание продукта"
          :invalid="errors.description"
          @input="clearError('description')"
        />
        <span v-if="errors.description" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="inline-form-group">
        <base-label :required="true">Код / артикул</base-label>
        <base-input
          v-model="form.code"
          placeholder="Код или артикул"
          :invalid="errors.code"
          @input="clearError('code')"
        />
        <span v-if="errors.code" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="inline-form-group">
        <base-label :required="true">Цена по умолчанию</base-label>
        <base-input
          v-model="form.price"
          type="number"
          step="0.01"
          min="0"
          placeholder="0.00"
          :invalid="errors.price"
          @input="clearError('price')"
        />
        <span v-if="errors.price" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="inline-form-group">
        <base-label :required="true">Единица измерения</base-label>
        <base-input
          v-model="form.unit"
          placeholder="шт, кг, литров..."
          :invalid="errors.unit"
          @input="clearError('unit')"
        />
        <span v-if="errors.unit" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <VatFields
        v-model:vatKind="form.vat_kind"
        v-model:vatRate="form.vat_rate"
        v-model:vatCalculationType="form.vat_calculation_type"
        :vat-kind-options="vatKindOptions"
        :vat-calculation-type-options="vatCalculationTypeOptions"
        :errors="errors"
        field-class="inline-form-group"
        @clear-error="clearError"
      />
    </div>

    <div class="inline-form-actions">
      <base-primary-button
        @click="save"
        :loading="isLoading"
        class="btn-sm"
      >
        {{ submitLabel }}
      </base-primary-button>
      <base-button @click="$emit('cancel')" class="btn-sm">
        Отмена
      </base-button>
    </div>
  </div>
</template>

<script>
import { useNotificationStore } from "@/stores/notificationStore";
import VatFields from "@/components/Shared/VatFields.vue";

import {
  payloadVatRate,
  vatRateIsInvalid,
} from "@/utils/vatUtils";

export default {
  name: "ProductForm",
  components: { VatFields },
  props: {
    vatKindOptions: { type: Array, required: true },
    vatCalculationTypeOptions: { type: Array, required: true },
    initialData: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    submitLabel: { type: String, default: "Сохранить" },
  },
  emits: ["save", "cancel"],
  data() {
    return {
      form: {
        name: "",
        description: "",
        code: "",
        unit: "",
        price: "",
        vat_kind: null,
        vat_rate: "",
        vat_calculation_type: null,
      },
      errors: {
        name: false,
        description: false,
        code: false,
        unit: false,
        price: false,
        vat_kind: false,
        vat_rate: false,
        vat_calculation_type: false,
      },
    };
  },
  computed: {
    notificationStore() {
      return useNotificationStore();
    },
  },
  methods: {
    initForm() {
      if (!this.initialData) return;
      this.form = {
        name: this.initialData.name || "",
        description: this.initialData.description || "",
        code: this.initialData.code || "",
        unit: this.initialData.unit || "",
        price: this.initialData.price || "",
        vat_kind: this.initialData.vat_kind || null,
        vat_rate: this.initialData.vat_rate ?? "",
        vat_calculation_type:
          this.initialData.vat_calculation_type || null,
      };
    },

    clearError(field) {
      if (this.errors[field]) {
        this.errors[field] = false;
      }
    },

    validate() {
      let isValid = true;
      this.errors = {
        name: false,
        description: false,
        code: false,
        unit: false,
        price: false,
        vat_kind: false,
        vat_rate: false,
        vat_calculation_type: false,
      };

      if (!String(this.form.name).trim()) {
        this.errors.name = true;
        isValid = false;
      }
      if (!String(this.form.description).trim()) {
        this.errors.description = true;
        isValid = false;
      }
      if (!String(this.form.code).trim()) {
        this.errors.code = true;
        isValid = false;
      }
      if (!String(this.form.unit).trim()) {
        this.errors.unit = true;
        isValid = false;
      }
      if (
        this.form.price === "" ||
        this.form.price === null ||
        parseFloat(this.form.price) < 0
      ) {
        this.errors.price = true;
        isValid = false;
      }
      if (!this.form.vat_kind) {
        this.errors.vat_kind = true;
        isValid = false;
      }
      if (this.form.vat_kind === "with_vat") {
        if (
          vatRateIsInvalid(this.form.vat_kind, this.form.vat_rate, {
            allowZeroWithVat: true,
          }) ||
          parseFloat(this.form.vat_rate) > 100
        ) {
          this.errors.vat_rate = true;
          isValid = false;
        }
      }
      if (!this.form.vat_calculation_type) {
        this.errors.vat_calculation_type = true;
        isValid = false;
      }

      if (!isValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );
      }

      return isValid;
    },

    save() {
      if (!this.validate()) return;

      this.$emit("save", {
        name: this.form.name,
        description: this.form.description,
        code: this.form.code,
        unit: this.form.unit,
        price: parseFloat(this.form.price),
        vat_kind: this.form.vat_kind,
        vat_rate: payloadVatRate(this.form.vat_kind, this.form.vat_rate, {
          zeroKindAsZero: false,
        }),
        vat_calculation_type: this.form.vat_calculation_type,
      });
    },
  },
  created() {
    this.initForm();
  },
};
</script>

<style scoped>
.inline-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: var(--base-in-indent);
}

.inline-form-group {
  display: flex;
  flex-direction: column;
}

.inline-form-group--wide {
  grid-column: span 2;
}

.inline-form-actions {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 14px;
  font-size: 13px;
}

.error-message {
  margin-top: 5px;
  color: var(--color-danger);
  font-size: 12px;
}

@media (max-width: 600px) {
  .inline-form-grid {
    grid-template-columns: 1fr;
  }

  .inline-form-group--wide {
    grid-column: span 1;
  }
}
</style>
